import React, { useState } from 'react';
import { Mail } from 'lucide-react';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/xrebnyqj', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        setStatus('success');
        form.reset();
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden py-20">
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 sm:mb-6 tracking-tight">
            Get In Touch
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed px-2">
            Ready to start your next project or have a question? Fill out the form below and our team will get back to you within one business day.
          </p>
        </div>

          <div className="flex justify-center mb-8 sm:mb-10">
          {/* Contact Information Card */}
          <div 
            className="rounded-2xl p-5 sm:p-6 text-center w-full max-w-sm"
            style={{
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="flex justify-center mb-3 sm:mb-4">
              <Mail className="text-white" size={28} style={{ width: 'auto', height: 'auto' }} />
            </div>
            <h3 className="text-white font-semibold text-base sm:text-lg mb-2">Email</h3>
            <a 
              href="mailto:inquire@syltix.dev" 
              className="text-white/80 hover:text-white transition-colors text-sm sm:text-base break-all"
            >
              inquire@syltix.dev
            </a>
          </div>
        </div>

        {/* Contact Form */}
        <form 
          onSubmit={handleSubmit}
          className="space-y-6 rounded-2xl p-6 sm:p-10"
          style={{
            background: 'rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
          }}
        >
          <div>
            <label className="block text-white/90 font-semibold text-base sm:text-lg mb-2">
              Name
            </label>
            <input 
              type="text" 
              name="name" 
              required 
              className="w-full rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder-white/50 transition-all duration-200" 
              placeholder="Your name"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
              onFocus={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              }}
            />
          </div>

          <div>
            <label className="block text-white/90 font-semibold text-base sm:text-lg mb-2">
              Email
            </label>
            <input 
              type="email" 
              name="email" 
              required 
              className="w-full rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder-white/50 transition-all duration-200" 
              placeholder="your.email@example.com"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
              onFocus={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              }}
            />
          </div>

          <div>
            <label className="block text-white/90 font-semibold text-base sm:text-lg mb-2">
              Message
            </label>
            <textarea 
              name="message" 
              required 
              rows={5}
              className="w-full rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white placeholder-white/50 transition-all duration-200 resize-none" 
              placeholder="Tell us about your project..."
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                minHeight: '120px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              }}
            />
          </div>

          <button 
            type="submit" 
            disabled={status === 'loading'}
            className="w-full flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg text-white transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{
              background: 'rgba(255, 255, 255, 0.18)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
            }}
            onMouseEnter={(e) => {
              if (status !== 'loading') {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
            }}
          >
            <Mail size={20} />
            {status === 'loading' ? 'Sending...' : status === 'success' ? 'Message Sent!' : 'Send Message'}
          </button>
          
          {status === 'success' && (
            <div className="text-center text-sm text-green-400 mt-4">
              Thank you! Your message has been sent successfully.
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-center text-sm text-red-400 mt-4">
              Something went wrong. Please try again or email us directly.
            </div>
          )}
        </form>

        <div className="mt-8 text-center text-sm text-white/60">
          We respect your privacy. Your information will never be shared.
        </div>
      </div>
    </section>
  );
} 